import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merges class names and resolves Tailwind conflicts, so a caller's `px-6` wins
 * over a component's default `px-4` instead of both landing in the class list.
 */
export const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs));

export const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatRelative = (value: string | null | undefined): string => {
  if (!value) {
    return 'never';
  }

  const deltaMs = Date.now() - new Date(value).getTime();
  const minutes = Math.round(deltaMs / 60_000);

  if (minutes < 1) {
    return 'just now';
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.round(hours / 24);
  if (days < 30) {
    return `${days}d ago`;
  }

  return formatDate(value);
};

export const formatCompactNumber = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    notation: value >= 1_000 ? 'compact' : 'standard',
    maximumFractionDigits: value >= 1_000 ? 1 : 0,
  }).format(value);

export const formatCurrencyMinor = (
  valueMinor: number,
  currency: string,
  compact = false,
): string => {
  try {
    const base = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    });
    const fractionDigits = base.resolvedOptions().maximumFractionDigits ?? 2;
    const value = valueMinor / 10 ** fractionDigits;
    const useCompactNotation = compact && Math.abs(value) >= 1_000;

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      notation: useCompactNotation ? 'compact' : 'standard',
      maximumFractionDigits: useCompactNotation ? 1 : fractionDigits,
    }).format(value);
  } catch {
    return `${currency.toUpperCase()} ${(valueMinor / 100).toFixed(2)}`;
  }
};

export const calculatePercentChange = (
  current: number,
  previous: number,
): number | null => {
  if (previous === 0) {
    return current === 0 ? 0 : null;
  }

  return ((current - previous) / previous) * 100;
};
